// web/src/routes/CreateScreen.tsx
// Figma re-skin (node 2001:2 "roast" / 2007:838 "toast"): composes the giant
// mode wordmark hero + top-center RoastCharacter + the frosted "my deck"
// SourceSlot + the binary RoastToastToggle + the "do it." CTA into the
// redesigned Create screen, builds the CreateRequest on Generate, writes it
// to the create-flow store, and hands off to the (placeholder) /processing
// route.
import { useEffect, useState } from "react";
import type { CSSProperties, JSX } from "react";
import { AnimatePresence, motion, useAnimationControls, useReducedMotion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { ModeVideoBackground } from "@/components/ModeVideoBackground";
import { SourceSlot, type LoadedSource } from "@/components/SourceSlot";
import { RoastToastToggle } from "@/components/RoastToastToggle";
import BorderGlow from "@/components/BorderGlow";
import { ROAST_PATHS, TOAST_PATHS, WORDMARK_VIEWBOX } from "@/components/wordmarkPaths";
import { HostCharacter } from "@/player/HostCharacter";
import { getCharacter } from "@/config/characters";
import { DEFAULT_VOICE } from "@/config/voices";
import { createFlow, type CreateRequest } from "@/lib/createFlow";
import { prefetchQuips, cancelQuips } from "@/lib/quipPrefetch";
import { playTap, playModeSound, playDoIt, playUndertakerBell } from "@/lib/sfx";
import { logout } from "@/lib/auth";
import type { Mode } from "@/types/roast";
import { cn } from "@/lib/utils";
import { EXTREME_DIRECTIVE } from "@/config/extremeMode";
import { useWordmarkFx } from "@/lib/wordmarkFx";
import WordmarkFxDebug from "@/components/dev/WordmarkFxDebug";

/** Giant hero-wordmark gradient per mode — exact color stops pulled from the
 *  Figma "roast"/"toast" frames. `dir` is the vertical direction of the fill
 *  ("down" = top→bottom, "up" = bottom→top). */
const WORDMARK_STOPS: Record<Mode, { dir: "down" | "up"; stops: [string, number][] }> = {
  roast: { dir: "down", stops: [["#280606", 36.34], ["#49070a", 49.32], ["#e62c36", 58.79], ["#d65f1f", 77.18]] },
  toast: { dir: "up", stops: [["#456fdd", 37.1], ["#083783", 48.15]] },
};

/** The hero mode wordmark rendered from the user's pre-aligned Figma outlines
 *  (wordmarkPaths.ts): "roast"/"toast" share one viewBox with identical o/a/s/t
 *  geometry, so they overlay with zero shift on toggle. We keep the app's
 *  gradient fills and only use the Figma export for the path shapes. */
const WORDMARK_PATHS: Record<Mode, readonly string[]> = { roast: ROAST_PATHS, toast: TOAST_PATHS };
const WORDMARK_H = 1113; // viewBox height of WORDMARK_VIEWBOX ("0 0 3559 1113")

/** Hero character: pinned near the top of the viewport and allowed to overlap
 *  the wordmark. Fixed size (same at every breakpoint), centered horizontally. */
const CHAR_SIZE_PX = 430;
const CHAR_TOP = "0.5vh";

// Roast heat-haze + flicker, toast sheen — all tunable live via the wordmarkFx store
// (dev panel: WordmarkFxDebug, backtick to toggle). Effects are motion → fully OFF
// under prefers-reduced-motion (clean static word). Prod ships WORDMARK_FX_DEFAULTS.
function MoodWordmark({ mode }: { mode: Mode }): JSX.Element {
  const g = WORDMARK_STOPS[mode];
  const gid = `wordmark-${mode}`;
  const reduce = useReducedMotion();
  const fx = useWordmarkFx();
  const heatOn = mode === "roast" && !reduce && fx.enabled;
  const sheenOn = mode === "toast" && !reduce && fx.enabled;
  const sheenRad = (fx.sheenAngle * Math.PI) / 180; // sheen band tilt
  return (
    <svg
      data-testid="mode-wordmark"
      data-mode={mode}
      aria-hidden="true"
      className={`block w-full${heatOn ? " wordmark-flicker" : ""}`}
      style={heatOn ? ({ "--wm-flicker": fx.flicker } as CSSProperties) : undefined}
      viewBox={WORDMARK_VIEWBOX}
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        {/* One gradient across the whole word (userSpaceOnUse over the viewBox
            height) — not per-letter — matching the Figma fill. */}
        <linearGradient
          id={gid}
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1={g.dir === "down" ? 0 : WORDMARK_H}
          x2="0"
          y2={g.dir === "down" ? WORDMARK_H : 0}
        >
          {g.stops.map(([color, offset]) => (
            <stop key={offset} offset={`${offset}%`} stopColor={color} />
          ))}
        </linearGradient>

        {/* Roast heat-haze: fractal-noise turbulence displaces the letters; the noise
            morphs slowly (vertical-biased) → a rising shimmer. */}
        {heatOn && (
          <filter
            id="roast-heat"
            filterUnits="userSpaceOnUse"
            primitiveUnits="userSpaceOnUse"
            x={-100}
            y={-150}
            width={3759}
            height={1823}
            colorInterpolationFilters="sRGB"
          >
            {/* Seamless noise tile → feTile repeat → feOffset scrolled UPWARD at a
                constant (linear) speed, looping by exactly one tile → the heat rises
                continuously (no ping-pong, no ease). */}
            <feTurbulence
              type="fractalNoise"
              baseFrequency={`${fx.heatFreqX} ${fx.heatFreqY}`}
              numOctaves="2"
              seed="7"
              stitchTiles="stitch"
              result="tile"
              x={0}
              y={0}
              width={fx.heatTile}
              height={fx.heatTile}
            />
            <feTile in="tile" result="tiled" />
            <feOffset in="tiled" result="scroll" dy={0}>
              <animate
                attributeName="dy"
                values={`0;${-fx.heatTile}`}
                keyTimes="0;1"
                dur={`${fx.heatDur}s`}
                repeatCount="indefinite"
                calcMode="linear"
              />
            </feOffset>
            <feDisplacementMap in="SourceGraphic" in2="scroll" scale={fx.heatScale} xChannelSelector="R" yChannelSelector="G" />
          </filter>
        )}

        {/* Toast sheen: a narrow warm highlight band swept across, then a pause. */}
        {sheenOn && (
          <linearGradient
            id="toast-sheen"
            gradientUnits="userSpaceOnUse"
            x1="0"
            y1="0"
            x2={fx.sheenWidth * Math.cos(sheenRad)}
            y2={fx.sheenWidth * Math.sin(sheenRad)}
          >
            <stop offset="0" stopColor="#fff7e0" stopOpacity="0" />
            <stop offset="0.42" stopColor="#fff7e0" stopOpacity="0" />
            <stop offset="0.5" stopColor="#ffe7a8" stopOpacity={fx.sheenOpacity} />
            <stop offset="0.58" stopColor="#fff7e0" stopOpacity="0" />
            <stop offset="1" stopColor="#fff7e0" stopOpacity="0" />
            <animateTransform
              attributeName="gradientTransform"
              type="translate"
              dur={`${fx.sheenDur}s`}
              repeatCount="indefinite"
              calcMode="spline"
              keyTimes="0;0.5;1"
              keySplines="0.85 0 1 1;0 0 1 1"
              values={`${-fx.sheenWidth} 0;3559 0;3559 0`}
              begin={`${-fx.sheenStart * fx.sheenDur}s`}
            />
          </linearGradient>
        )}

        {/* Secondary sheen (brighter, trails by outlineDelay) for the offset outline. */}
        {sheenOn && fx.outline && (
          <linearGradient
            id="toast-sheen2"
            gradientUnits="userSpaceOnUse"
            x1="0"
            y1="0"
            x2={fx.sheenWidth * Math.cos(sheenRad)}
            y2={fx.sheenWidth * Math.sin(sheenRad)}
          >
            <stop offset="0" stopColor="#fffdf5" stopOpacity="0" />
            <stop offset="0.44" stopColor="#fffdf5" stopOpacity="0" />
            <stop offset="0.5" stopColor="#ffffff" stopOpacity={fx.sheenOpacity} />
            <stop offset="0.56" stopColor="#fffdf5" stopOpacity="0" />
            <stop offset="1" stopColor="#fffdf5" stopOpacity="0" />
            <animateTransform
              attributeName="gradientTransform"
              type="translate"
              dur={`${fx.sheenDur}s`}
              repeatCount="indefinite"
              calcMode="spline"
              keyTimes="0;0.5;1"
              keySplines="0.85 0 1 1;0 0 1 1"
              values={`${-fx.sheenWidth} 0;3559 0;3559 0`}
              begin={`${(fx.outlineDelay - fx.sheenStart) * fx.sheenDur}s`}
            />
          </linearGradient>
        )}
      </defs>

      <g fill={`url(#${gid})`} filter={heatOn ? "url(#roast-heat)" : undefined}>
        {WORDMARK_PATHS[mode].map((d, i) => (
          <path key={i} d={d} />
        ))}
      </g>

      {/* Sheen overlay: same letters filled with the moving highlight, screen-blended. */}
      {sheenOn && (
        <g fill="url(#toast-sheen)" style={{ mixBlendMode: "screen" }}>
          {WORDMARK_PATHS[mode].map((d, i) => (
            <path key={`sheen-${i}`} d={d} />
          ))}
        </g>
      )}

      {/* Secondary sheen on an offset letter outline (trails the fill sheen). */}
      {sheenOn && fx.outline && (
        <g
          transform={`translate(${fx.outlineOffset} ${fx.outlineOffset})`}
          fill="none"
          stroke="url(#toast-sheen2)"
          strokeWidth={fx.outlineWidth}
          style={{ mixBlendMode: "screen" }}
        >
          {WORDMARK_PATHS[mode].map((d, i) => (
            <path key={`outline-${i}`} d={d} />
          ))}
        </g>
      )}
    </svg>
  );
}

/** "do it." CTA pill fill/border per mode; the inner warm glow is shared
 *  across both modes (verified against both Figma frames). */
const CTA_COLOR: Record<Mode, { bg: string; border: string }> = {
  roast: { bg: "#e62c36", border: "#ff715e" },
  toast: { bg: "#2c71e6", border: "#5eecff" },
};

/** BorderGlow tuning for the "do it." CTA per mode: `glowColor` is "H S L" for the
 *  outer glow; `colors` are the 3 mesh-gradient border hues that bloom on hover. */
const CTA_GLOW: Record<Mode, { glowColor: string; colors: string[] }> = {
  roast: { glowColor: "14 90 62", colors: ["#ff9a5b", "#e62c36", "#ffd06b"] },
  toast: { glowColor: "212 90 64", colors: ["#7fd0ff", "#2c71e6", "#a9c9ff"] },
};

const WATERMARK_COLOR: Record<Mode, string> = {
  roast: "rgba(230,44,54,0.42)",
  toast: "#e381b1",
};

export default function CreateScreen(): JSX.Element {
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>("roast");
  const [loaded, setLoaded] = useState<LoadedSource | null>(null);
  const [extreme, setExtreme] = useState(false);

  // Spring "poke" the CTA when it's clicked (mirrors the toggle + source card).
  const ctaControls = useAnimationControls();
  const pokeCta = () => {
    ctaControls.set({ scale: 0.95 });
    void ctaControls.start({ scale: 1 }, { type: "spring", stiffness: 460, damping: 12 });
  };

  // Each fresh visit to /create starts clean — otherwise a stale CreateRequest
  // left over from a prior session could get reprocessed by a later nav to
  // /processing.
  useEffect(() => {
    createFlow.clear();
  }, []);

  // Common UI feedback: a tap sound on any button click within the screen — but
  // not the mode toggle (role="radio"), which gets its own reaction sound.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const btn = (e.target as Element | null)?.closest?.("button");
      // Skip the toggle (role=radio) and buttons with their own SFX (data-sfx-skip).
      if (btn && btn.getAttribute("role") !== "radio" && !btn.hasAttribute("data-sfx-skip")) playTap();
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  // A mode is always selected (default roast, no neutral) — Generate only
  // waits on a loaded source.
  const canGenerate = !!loaded;

  // Speculative quip prefetch: fire as soon as a source is loaded (and on mode flip),
  // masking the ~3s call behind the user's dwell so quips are warm in /processing.
  // profanity is fixed "savage" on this screen. Removing the source cancels it.
  useEffect(() => {
    if (loaded) prefetchQuips(loaded.file, loaded.detectedType, mode, "savage");
    else cancelQuips();
  }, [loaded, mode]);

  const handleGenerate = () => {
    if (!loaded) return;
    const req: CreateRequest = {
      source: loaded.file,
      detectedType: loaded.detectedType,
      mode,
      profanity: "savage",
      doodle: true,
      pageCount: loaded.pageCount,
      voiceId: DEFAULT_VOICE[mode],
      roastId: loaded.roastId,
      aspect: loaded.aspect,
      coverBlob: loaded.coverBlob,
      name: null,
      context: extreme ? EXTREME_DIRECTIVE[mode] : null,
      length: "deepFry",
    };
    createFlow.set(req);
    // viewTransition: the loaded thumbnail (view-transition-name "source-thumb")
    // morphs seamlessly into the /processing screen's thumbnail.
    navigate("/processing", { viewTransition: true });
  };

  // Apply .toast-mode class for the mode theme swap (mirrors StudioCreate).
  const wrapperClass = cn(
    "relative flex min-h-screen w-full flex-col items-center overflow-hidden text-white",
    mode === "toast" && "toast-mode",
  );

  const cta = CTA_COLOR[mode];

  return (
    <div className={wrapperClass}>
      <ModeVideoBackground pos={mode === "roast" ? 1 : -1} />
      {import.meta.env.DEV && <WordmarkFxDebug />}

      {/* Giant mode wordmark hero — full viewport width, vertical center at 35%.
          The words are pre-aligned outlines (identical geometry), so a quick
          crossfade swaps them cleanly — the shared o/a/s/t sit still and only
          the first glyph (r↔t) dissolves. */}
      <div className="pointer-events-none absolute inset-x-0 top-[35%] z-0 grid -translate-y-1/2 select-none">
        <AnimatePresence initial={false}>
          <motion.div
            key={mode}
            className="[grid-area:1/1]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
          >
            <MoodWordmark mode={mode} />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Hero character — the "Host" artboard, switched via the "Character" enum
          (no remount). Pinned near the top of the viewport (centered), allowed to
          overlap the wordmark; fixed size at every breakpoint. */}
      <div
        className="pointer-events-none absolute inset-x-0 z-10 mx-auto"
        style={{ top: CHAR_TOP, height: `${CHAR_SIZE_PX}px`, width: `${CHAR_SIZE_PX}px` }}
      >
        <HostCharacter src={getCharacter("roast").riveSource} mode={mode} />
      </div>

      <div className="relative z-10 flex min-h-screen w-full flex-col items-center px-4 py-8">
        <div className="mt-auto flex w-full max-w-sm flex-col items-center gap-6 pb-6">
          <SourceSlot onLoaded={setLoaded} mode={mode} />

          <RoastToastToggle
            mode={mode}
            extreme={extreme}
            onChange={(m) => {
              if (m !== mode) playModeSound(m); // roast → alert, toast → rizz
              setMode(m);
            }}
            onToggleExtreme={() =>
              setExtreme((on) => {
                const next = !on;
                if (next) playUndertakerBell(); // toll only on entry, not exit
                return next;
              })
            }
          />

          {canGenerate ? (
            <motion.div className="w-full max-w-xs" animate={ctaControls}>
              <BorderGlow
                className="w-full"
                backgroundColor={cta.bg}
                borderRadius={100}
                glowColor={CTA_GLOW[mode].glowColor}
                colors={CTA_GLOW[mode].colors}
                glowRadius={36}
                glowIntensity={1}
              >
                <button
                  type="button"
                  data-sfx-skip
                  onClick={() => { playDoIt(); pokeCta(); handleGenerate(); }}
                  className="w-full py-4 text-center text-2xl font-bold text-white"
                >
                  do it.
                </button>
              </BorderGlow>
            </motion.div>
          ) : (
            <button
              type="button"
              disabled
              className="w-full max-w-xs cursor-not-allowed rounded-full border-2 py-4 text-2xl font-bold text-white opacity-40"
              style={{ backgroundColor: cta.bg, borderColor: cta.border }}
            >
              do it.
            </button>
          )}
        </div>
      </div>

      {/* Sign out (replaces the old internal go/ watermark). Logout flips `corp` →
          RequireAuth redirects to `/` → back to the speakeasy door. */}
      <button
        type="button"
        title="Sign out"
        onClick={() => { playTap(); void logout(); }}
        className="absolute bottom-4 left-4 z-10 text-sm font-medium tracking-wide transition hover:brightness-150"
        style={{ color: WATERMARK_COLOR[mode] }}
      >
        Sign out
      </button>
    </div>
  );
}
